import React, { useEffect, useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { ReactComponent as LinkedinIcon } from "feather-icons/dist/icons/linkedin.svg";
import { ReactComponent as TwitterIcon } from "feather-icons/dist/icons/twitter.svg";
import { ReactComponent as FacebookIcon } from "feather-icons/dist/icons/facebook.svg";
import { ReactComponent as MailIcon } from "feather-icons/dist/icons/mail.svg";
import { ReactComponent as LinkIcon } from "feather-icons/dist/icons/link.svg";
import { ReactComponent as CheckIcon } from "feather-icons/dist/icons/check.svg";
import { ReactComponent as ShareIcon } from "feather-icons/dist/icons/share-2.svg";

/** Boutons de partage (LinkedIn, X, Facebook, e-mail, copie du lien, partage natif sur mobile). */

const Wrapper = tw.div`flex items-center flex-wrap`;
const Label = tw.span`uppercase text-xs font-bold tracking-widest text-gray-900 mr-4`;
const Buttons = tw.div`flex items-center flex-wrap`;
const Button = styled.a`
  ${tw`w-10 h-10 mr-2 mb-2 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center transition duration-300`}
  ${tw`hocus:bg-primary-500 hocus:text-gray-100 focus:outline-none focus:shadow-outline`}
  svg {
    ${tw`w-4 h-4`}
  }
`;
const Feedback = tw.span`ml-2 mb-2 text-sm font-semibold text-green-700`;

const copyText = async text => {
  if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "absolute";
  area.style.left = "-9999px";
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand && document.execCommand("copy");
  document.body.removeChild(area);
  return Boolean(ok);
};

export default function ShareBar({ url, title, text }) {
  const [copied, setCopied] = useState(false);
  const [canShare, setCanShare] = useState(false);

  useEffect(() => {
    setCanShare(typeof navigator !== "undefined" && typeof navigator.share === "function");
  }, []);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(t);
  }, [copied]);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title || "");
  const mailBody = encodeURIComponent(`${title || ""}\n\n${url}`);

  const handleCopy = async event => {
    event.preventDefault();
    try {
      if (await copyText(url)) setCopied(true);
    } catch (e) {
      setCopied(false);
    }
  };

  const handleNativeShare = async event => {
    event.preventDefault();
    try {
      await navigator.share({ title, text: text || title, url });
    } catch (e) {
      /* partage annulé par l'utilisateur : rien à faire */
    }
  };

  return (
    <Wrapper>
      <Label>Partager</Label>
      <Buttons>
        <Button href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`} target="_blank" rel="noopener noreferrer" aria-label="Partager sur LinkedIn" title="LinkedIn">
          <LinkedinIcon aria-hidden="true" />
        </Button>
        <Button href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`} target="_blank" rel="noopener noreferrer" aria-label="Partager sur X (Twitter)" title="X (Twitter)">
          <TwitterIcon aria-hidden="true" />
        </Button>
        <Button href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} target="_blank" rel="noopener noreferrer" aria-label="Partager sur Facebook" title="Facebook">
          <FacebookIcon aria-hidden="true" />
        </Button>
        <Button href={`mailto:?subject=${encodedTitle}&body=${mailBody}`} aria-label="Partager par e-mail" title="E-mail">
          <MailIcon aria-hidden="true" />
        </Button>
        <Button href={url} onClick={handleCopy} aria-label="Copier le lien de l'article" title="Copier le lien">
          {copied ? <CheckIcon aria-hidden="true" /> : <LinkIcon aria-hidden="true" />}
        </Button>
        {canShare && (
          <Button href={url} onClick={handleNativeShare} aria-label="Partager…" title="Partager…">
            <ShareIcon aria-hidden="true" />
          </Button>
        )}
        <span aria-live="polite">{copied && <Feedback>Lien copié !</Feedback>}</span>
      </Buttons>
    </Wrapper>
  );
}
